import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * Root mobile menu drawer is expanded (panel shown). Excludes the post-close slide animation
 * (`data-drawer-closing`) so sticky chrome can return as soon as the user closes.
 * @returns {boolean}
 */
function isMainMenuDrawerOpen() {
  const details = document.getElementById('Details-menu-drawer-container');
  return details instanceof HTMLDetailsElement && details.classList.contains('menu-open');
}

/**
 * Viewport Y of the bottom edge of the header chrome (announcement + header at top, header only when scrolled).
 * Used for fixed menu drawer inset; not the static layout height of #header-group.
 * @param {HTMLElement | null} [header]
 */
export function updateHeaderVisibleBottom(header = document.querySelector('header-component')) {
  if (!(header instanceof HTMLElement)) return;

  const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
  document.body.style.setProperty('--header-visible-bottom', `${bottom}px`);
}

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);
    updateHeaderVisibleBottom(this);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when it has moved offscreen
   * for the scroll-up sticky behavior.
   */
  #observeStickyPosition = () => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;
      this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (isMainMenuDrawerOpen()) {
      this.dataset.stickyState = 'active';
      if (this.#timeout) {
        clearTimeout(this.#timeout);
        this.#timeout = null;
      }
      if (stickyMode === 'always') {
        if (isAtTop) {
          this.dataset.scrollDirection = 'none';
        } else if (isScrollingUp) {
          this.dataset.scrollDirection = 'up';
        } else {
          this.dataset.scrollDirection = 'down';
        }
      }
      if (stickyMode === 'scroll-up') {
        this.#offscreen = true;
      }
      this.#lastScrollTop = scrollTop;
      updateHeaderVisibleBottom(this);
      return;
    }

    if (!this.#offscreen && stickyMode !== 'always') return;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      this.dataset.stickyState = scrollTop > 0 ? 'active' : 'inactive';

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      updateHeaderVisibleBottom(this);
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
    updateHeaderVisibleBottom(this);
  };

  /**
   * Restores `data-sticky-state` (and scroll-up `#offscreen`) after the mobile menu drawer fully closes.
   * @param {string | null} snapshot `getAttribute('data-sticky-state')` before open, or null if absent.
   */
  reconcileStickyAfterDrawer(snapshot) {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    if (stickyMode === 'always') {
      if (snapshot != null && snapshot !== '') {
        this.dataset.stickyState = snapshot;
      } else {
        this.removeAttribute('data-sticky-state');
      }
      updateHeaderVisibleBottom(this);
      return;
    }

    if (stickyMode === 'scroll-up') {
      const scrollTop = document.scrollingElement?.scrollTop ?? 0;
      this.#lastScrollTop = scrollTop;

      if (snapshot === 'inactive' || snapshot == null) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
        updateHeaderVisibleBottom(this);
        return;
      }

      this.#offscreen = true;
      this.dataset.stickyState = snapshot;
      this.#updateScrollState();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    updateHeaderVisibleBottom(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      if (stickyMode === 'scroll-up') {
        this.#observeStickyPosition();
      }

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#updateScrollState();
        window.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    window.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
    document.body.style.removeProperty('--header-visible-bottom');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  /** @type {number | null} */
  let visibleBottomRafId = null;
  const scheduleHeaderVisibleBottomUpdate = () => {
    if (visibleBottomRafId !== null) return;
    visibleBottomRafId = requestAnimationFrame(() => {
      visibleBottomRafId = null;
      updateHeaderVisibleBottom(header instanceof HTMLElement ? header : null);
    });
  };

  if (header instanceof HTMLElement) {
    updateHeaderVisibleBottom(header);
    document.addEventListener('scroll', scheduleHeaderVisibleBottomUpdate, { passive: true });
    window.addEventListener('resize', scheduleHeaderVisibleBottomUpdate);
  }

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
